import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type Viewport = "desktop" | "phone";

/** `null` means "whatever the window is", which is the normal case on both surfaces. */
export type ViewportOverride = Viewport | null;

const STORAGE_KEY = "factorium.viewport";

/**
 * Below this the desktop shell stops fitting: the handoff's rail (60) plus its navigator
 * panel (252) plus a content frame worth reading is already past 700, so anything narrower
 * is a phone whatever it calls itself. Tablets keep the desktop shell, which is what the
 * handoff draws for them — there is no third layout.
 */
export const PHONE_MAX_WIDTH = 767;

interface ViewportContextValue {
  /** Which shell is on screen right now. */
  viewport: Viewport;
  /** What the window itself is, ignoring any override. */
  measured: Viewport;
  override: ViewportOverride;
  setOverride: (next: ViewportOverride) => void;
  /**
   * Whether the phone shell is being *previewed* inside a desktop window, in which case it
   * is drawn in the handoff's 390 × 844 device frame with a mock status bar. On a real
   * phone it is false and the shell fills the viewport — the status bar there is the
   * operating system's, and drawing a second one saying 9:41 would be a lie in 38 pixels.
   */
  framed: boolean;
}

const ViewportContext = createContext<ViewportContextValue | null>(null);

/**
 * M12 — which of the two shells the app is wearing.
 *
 * The handoff selects the phone app with a toggle in the app bar, because a design
 * prototype has no phone to run on. A deployed PWA does, so the window decides by default
 * and the toggle becomes an override on top of it: a phone gets the phone app without
 * anybody choosing it, and the owner can still see either surface from either device.
 *
 * One thing this deliberately is *not*: a second app. Both shells render the same routes
 * from the same router, so a link works on both, a deep link restores the same place on
 * both, and there is exactly one URL space to keep honest.
 */
export function ViewportProvider({
  children,
  /** Test seam. Left alone, the window is measured. */
  initialOverride,
}: {
  children: React.ReactNode;
  initialOverride?: ViewportOverride;
}) {
  const [override, setOverrideState] = useState<ViewportOverride>(
    () => initialOverride ?? readStoredOverride(),
  );
  const [measured, setMeasured] = useState<Viewport>(measure);

  useEffect(() => {
    // `matchMedia` rather than a resize listener: the browser only calls back when the
    // answer actually changes, so rotating a phone does not re-render the app sixty times.
    if (typeof window.matchMedia !== "function") return;

    const query = window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH}px)`);
    const onChange = () => setMeasured(query.matches ? "phone" : "desktop");

    onChange();

    // `addListener` is the deprecated spelling, and it is what Safari before 14 has. A
    // phone milestone is the wrong place to assume the newer one.
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    }

    query.addListener?.(onChange);
    return () => query.removeListener?.(onChange);
  }, []);

  const setOverride = useCallback((next: ViewportOverride) => {
    setOverrideState(next);

    try {
      if (next) window.localStorage.setItem(STORAGE_KEY, next);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage disabled. The choice still holds for this session.
    }
  }, []);

  const value = useMemo<ViewportContextValue>(() => {
    const viewport = override ?? measured;

    return {
      viewport,
      measured,
      override,
      setOverride,
      framed: viewport === "phone" && measured === "desktop",
    };
  }, [measured, override, setOverride]);

  return <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>;
}

/**
 * Answers `desktop` outside a provider rather than throwing, unlike `useApp`.
 *
 * The two are different kinds of dependency: a page without a Server client can do nothing
 * at all, where a component without this one merely does not know it is on a phone — and
 * the honest answer to that is the shell every screen was written for first.
 */
export function useViewport(): ViewportContextValue {
  return (
    useContext(ViewportContext) ?? {
      viewport: "desktop",
      measured: "desktop",
      override: null,
      setOverride: () => undefined,
      framed: false,
    }
  );
}

function measure(): Viewport {
  if (typeof window.matchMedia !== "function") return "desktop";

  return window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH}px)`).matches ? "phone" : "desktop";
}

function readStoredOverride(): ViewportOverride {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "phone" || stored === "desktop" ? stored : null;
  } catch {
    return null;
  }
}
